<section class="p-5 lg:p-10">
  <div class="max-w-screen-xl mx-auto flex flex-col gap-6">
    <div class="flex flex-col gap-1">
      <h1 class="text-2xl font-bold text-chathams-blue">Questionários Personalizados</h1>
      <p class="text-gray-500 text-sm">
        Crie e edite roteiros personalizados para serem usados nos módulos configuráveis do dashboard.
      </p>
    </div>

    <div *ngIf="podeEscolherEmpresa" class="bg-white rounded-lg shadow p-5 border border-gray-100 flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-chathams-blue">Selecionar empresa</h2>
          <p class="text-xs text-gray-500">
            Escolha uma empresa para visualizar e editar os questionários vinculados a ela.
          </p>
        </div>
      </div>
      <ng-select
        [formControl]="companySelector"
        [items]="companies"
        bindLabel="name"
        bindValue="id"
        placeholder="Selecione uma empresa"
        notFoundText="Nenhuma empresa encontrada"
        [loading]="isCompanyLoading"
        [clearable]="true"
      >
      </ng-select>
      <small class="text-xs text-gray-500" *ngIf="!selectedCompanyId">
        Selecione uma empresa para carregar os questionários personalizados correspondentes.
      </small>
    </div>

    <div
      *ngIf="!podeEscolherEmpresa && !selectedCompanyId"
      class="p-4 border border-yellow-300 bg-yellow-50 rounded text-sm text-yellow-700"
    >
      Não foi possível identificar a empresa do usuário logado. Atribua uma empresa à sessão para gerenciar os
      questionários personalizados.
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" *ngIf="selectedCompanyId">
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-chathams-blue">Questionários existentes</h2>
          <button
            class="text-sm text-gray-500 hover:text-chathams-blue transition"
            (click)="loadQuestionnaires()"
            [disabled]="isLoading"
          >
            Atualizar lista
          </button>
        </div>

        <div *ngIf="isLoading" class="text-sm text-gray-500">Carregando questionários...</div>

        <ul class="flex flex-col gap-3 max-h-[520px] overflow-auto pr-2" *ngIf="questionnaires.length; else emptyState">
          <li
            *ngFor="let questionnaire of questionnaires; trackBy: trackByQuestionnaireId"
            class="p-4 bg-white rounded-lg shadow border border-gray-100 flex flex-col gap-2"
          >
            <div class="flex items-center justify-between gap-3">
              <div>
                <p class="text-base font-semibold text-chathams-blue">{{ questionnaire.name }}</p>
                <p class="text-xs uppercase tracking-wide text-gray-400">{{ questionnaire.category }}</p>
              </div>
              <span class="px-3 py-1 text-xs rounded-full bg-iceberg text-chathams-blue font-semibold">
                {{ questionnaire.questions.length || 0 }} perguntas
              </span>
            </div>

            <div class="flex gap-3">
              <button
                type="button"
                class="btn btn--secondary flex-1 h-9"
                *isAllowed="[ERoleAdminString.Admin, ERoleAdminString.QuestionnaireAdmin, 'questionnaire.edit']"
                (click)="startEditing(questionnaire)"
              >
                Editar
              </button>
              <button
                type="button"
                class="btn btn--primary flex-1 h-9 bg-cerise-red hover:bg-red-600"
                *isAllowed="[ERoleAdminString.Admin, ERoleAdminString.QuestionnaireAdmin, 'questionnaire.delete']"
                (click)="deleteQuestionnaire(questionnaire.id)"
              >
                Remover
              </button>
            </div>
          </li>
        </ul>

        <ng-template #emptyState>
          <div class="p-5 border border-dashed border-gray-300 rounded text-center text-sm text-gray-500">
            Ainda não há questionários personalizados cadastrados para esta empresa.
          </div>
        </ng-template>
      </div>

      <div class="p-5 bg-white rounded-lg shadow flex flex-col gap-4 border border-gray-100">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-lg font-semibold text-chathams-blue">
              {{ editingQuestionnaireId ? 'Editar questionário' : 'Novo questionário' }}
            </h2>
            <p class="text-xs text-gray-500">
              Defina um nome, categoria e as perguntas com suas respectivas alternativas.
            </p>
          </div>

          <button
            *ngIf="editingQuestionnaireId"
            class="text-xs text-gray-500 hover:text-chathams-blue"
            type="button"
            (click)="cancelEditing()"
          >
            Cancelar edição
          </button>
        </div>

        <form [formGroup]="form" (ngSubmit)="saveQuestionnaire()" class="flex flex-col gap-4">
          <div class="form-group">
            <label class="form-label text-sm font-semibold text-gray-500">Nome</label>
            <input type="text" class="form-input" placeholder="Ex.: Questionário inicial" formControlName="name" />
            <small class="text-xs text-cerise-red" *ngIf="isInvalid('name')">
              Informe um nome com pelo menos 3 caracteres.
            </small>
          </div>

          <div class="form-group">
            <label class="form-label text-sm font-semibold text-gray-500">Categoria</label>
            <input type="text" class="form-input" placeholder="Ex.: Saúde Mental" formControlName="category" />
            <small class="text-xs text-cerise-red" *ngIf="isInvalid('category')">
              Informe a categoria do questionário.
            </small>
          </div>

          <div formArrayName="questions" class="flex flex-col gap-4">
            <div
              *ngFor="let question of questions.controls; let questionIndex = index; trackBy: trackByIndex"
              [formGroupName]="questionIndex"
              class="border border-gray-200 rounded-lg p-4 bg-iceberg/40 flex flex-col gap-3"
            >
              <div class="flex items-center justify-between">
                <h3 class="text-base font-semibold text-chathams-blue">Pergunta {{ questionIndex + 1 }}</h3>
                <button
                  type="button"
                  class="text-xs text-cerise-red hover:underline"
                  (click)="removeQuestion(questionIndex)"
                >
                  Remover
                </button>
              </div>

              <input
                type="text"
                class="form-input"
                placeholder="Digite o texto da pergunta"
                formControlName="question"
              />

              <div formArrayName="answers" class="flex flex-col gap-2">
                <p class="text-xs font-semibold text-gray-500 uppercase">Alternativas</p>
                <div class="flex flex-col gap-2">
                  <div
                    *ngFor="
                      let answer of questionAnswers(questionIndex).controls;
                      let answerIndex = index;
                      trackBy: trackByIndex
                    "
                    [formGroupName]="answerIndex"
                    class="grid grid-cols-1 md:grid-cols-4 gap-2 items-center"
                  >
                    <input
                      type="text"
                      class="form-input md:col-span-3"
                      placeholder="Resposta"
                      formControlName="answer"
                    />
                    <button
                      type="button"
                      class="btn btn--primary bg-cerise-red hover:bg-red-600 h-9 md:col-span-1"
                      (click)="removeAnswer(questionIndex, answerIndex)"
                    >
                      Remover
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn--secondary h-9 w-full md:w-auto"
                  (click)="addAnswer(questionIndex)"
                >
                  + Adicionar alternativa
                </button>
              </div>
            </div>
          </div>

          <button type="button" class="btn btn--secondary h-9" (click)="addQuestion()">+ Adicionar pergunta</button>

          <div class="flex flex-col md:flex-row gap-3">
            <button
              class="btn btn--secondary h-10 md:flex-1"
              *isAllowed="[
                ERoleAdminString.Admin,
                ERoleAdminString.QuestionnaireAdmin,
                editingQuestionnaireId ? 'questionnaire.edit' : 'questionnaire.create'
              ]"
              type="submit"
              [disabled]="isSaving || form.invalid || !selectedCompanyId"
            >
              {{ editingQuestionnaireId ? 'Atualizar questionário' : 'Criar questionário' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</section>
